import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { intlPluralizeString, useDateShortRelative } from '@gitbook/react-uikit';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { Tooltip } from '@gitbook/spine-react';

/**
 * Details for a change request
 */
export function ChangeRequestDetails(props: {
    changeRequest: api.ChangeRequest;
}): React.ReactElement {
    const { changeRequest } = props;
    const createdAt = useDateShortRelative(changeRequest.createdAt, { onlyDate: true });
    const commentsLabel = `${changeRequest.comments} ${intlPluralizeString({
        count: changeRequest.comments ?? 0,
        singular: 'comment',
        plural: 'comments',
    })}`;
    // on predictable UI, we don't show the change request number. This allows us to run any tests that create CRs in parallel
    const changeRequestNumber = process.env.GITBOOK_PREDICTABLE_UI ? '#' : changeRequest.number;

    return (
        <>
            <ChangeRequestInfoIcon
                label={`#${changeRequestNumber}`}
                accessibilityLabel={`Change request number ${changeRequestNumber}`}
                tooltipLabel={`Change request #${changeRequestNumber}`}
                icon={Icon.ChangeRequest}
            />
            <ChangeRequestInfoIcon
                label={createdAt.value}
                accessibilityLabel={`Created on ${createdAt.accessibilityValue}`}
                tooltipLabel={createdAt.tooltip}
                icon={Icon.History}
            />
            <ChangeRequestInfoIcon
                label={`${changeRequest.comments}`}
                accessibilityLabel={commentsLabel}
                tooltipLabel={commentsLabel}
                icon={changeRequest.comments > 1 ? Icon.CommentThread : Icon.Comment}
            />
        </>
    );
}

interface ChangeRequestInfoIconProps {
    label: string;
    accessibilityLabel: string;
    tooltipLabel: string;
    icon: SVGIconComponent;
}

function ChangeRequestInfoIcon(props: ChangeRequestInfoIconProps): React.ReactElement {
    const { label, accessibilityLabel, tooltipLabel, icon: Icon } = props;

    return (
        <Tooltip label={tooltipLabel}>
            <div aria-label={accessibilityLabel} className="flex items-center gap-1 text-light">
                <Icon aria-hidden size={12} />
                <span aria-hidden className="text-ui-small">
                    {label}
                </span>
            </div>
        </Tooltip>
    );
}
